import { ExperimentActionType } from './action-types';
import { Constraint, VariableInfo } from '@/lib/PlannerDTOs';

// Traditional action creator if you need Redux actions
export const getExperiment = () => {
  return {
    type: ExperimentActionType.GET_EXPERIMENT,
    payload: {},
  };
};

export const setExperiment = (
  data: any,
  isLoading: boolean,
  error: Error | null,
) => {
  return {
    type: ExperimentActionType.SET_EXPERIMENT,
    payload: { data, isLoading, error },
  };
};

export const cloneExperiment = (
  data: any,
  isLoading: boolean,
  error: Error | null,
) => {
  return {
    type: ExperimentActionType.CLONE_EXPERIMENT,
    payload: { data, isLoading, error },
  };
};

export const setConfiguration = (data: any) => {
  return {
    type: ExperimentActionType.SET_CONFIGURATION,
    payload: { data },
  };
};

export const updateExperiment = (data: any) => {
  return {
    type: ExperimentActionType.UPDATE_EXPERIMENT,
    payload: { data },
  };
};

export const updateConfiguration = (data: any) => {
  return {
    type: ExperimentActionType.UPDATE_CONFIGURATION,
    payload: { data },
  };
};

export const createExperiment = (
  data: any,
  isLoading: boolean,
  error: Error | null,
) => {
  return {
    type: ExperimentActionType.CREATE_EXPERIMENT,
    payload: { data, isLoading, error },
  };
};

export const setAdaptiveExperiment = (
  data: any,
  isLoading: boolean,
  error: Error | null,
) => {
  return {
    type: ExperimentActionType.SET_ADAPTIVE_EXPERIMENT,
    payload: { data, isLoading, error },
  };
};

export const addExperimentVariable = (variable: VariableInfo) => {
  return {
    type: ExperimentActionType.ADD_EXPERIMENT_VARIABLE,
    payload: { variable },
  };
};

export const deleteExperimentVariable = (variableName: string) => {
  return {
    type: ExperimentActionType.DELETE_EXPERIMENT_VARIABLE,
    payload: { variableName },
  };
};

export const addTreatment = () => {
  return {
    type: ExperimentActionType.ADD_TREATMENT,
    payload: {},
  };
};

export const deleteTreatment = (index: number) => {
  return {
    type: ExperimentActionType.DELETE_TREATMENT,
    payload: { index },
  };
};

export const addConfiguration = () => {
  return {
    type: ExperimentActionType.ADD_CONFIGURATION,
    payload: {},
  };
};

export const deleteConfiguration = (index: number) => {
  return {
    type: ExperimentActionType.DELETE_CONFIGURATION,
    payload: { index },
  };
};

export const cloneConfiguration = (fromIndex: number, toIndex: number) => {
  return {
    type: ExperimentActionType.CLONE_CONFIGURATION,
    payload: { fromIndex, toIndex },
  };
};

export const editTreatment = (index: number, name: string) => {
  return {
    type: ExperimentActionType.EDIT_TREATMENT,
    payload: { index, name },
  };
};

export const updateAssignmentStrategy = (strategy: string) => {
  return {
    type: ExperimentActionType.UPDATE_ASSIGNMENT_STRATEGY,
    payload: { strategy },
  };
};

export const splitTreatmentsEvenly = () => {
  return {
    type: ExperimentActionType.SPLIT_TREATMENTS_EVENLY,
    payload: {},
  };
};

export const setVariableTechFamilies = (techFamilies: string[]) => {
  return {
    type: ExperimentActionType.SET_VARIABLE_TECH_FAMILIES,
    payload: { techFamilies },
  };
};

export const setVariables = (variables: VariableInfo[]) => {
  return {
    type: ExperimentActionType.SET_VARIABLES,
    payload: { variables },
  };
};

export const setAnalysis = (
  analysis: any,
  isLoading: boolean,
  error: Error | null,
) => {
  return {
    type: ExperimentActionType.SET_ANALYSIS,
    payload: { analysis, isLoading, error },
  };
};

export const updateAnalysisConfig = (analysisConfig: any) => {
  return {
    type: ExperimentActionType.UPDATE_ANALYSIS_CONFIG,
    payload: { analysisConfig },
  };
};

export const updateRestrictByConstraint = (constraint: Constraint) => {
  return {
    type: ExperimentActionType.UPDATE_RESTRICT_BY_CONSTRAINT,
    payload: { constraint },
  };
};

export const updateValidExperiment = (valid: boolean) => {
  return {
    type: ExperimentActionType.UPDATE_VALID_EXPERIMENT,
    payload: { valid },
  };
};

export const updateValidMetric = (valid: boolean) => {
  return {
    type: ExperimentActionType.UPDATE_VALID_METRIC,
    payload: { valid },
  };
};

export const addConstraint = (constraint: Constraint) => {
  return {
    type: ExperimentActionType.ADD_CONSTRAINT,
    payload: { constraint },
  };
};

export const deleteConstraint = (index: number) => {
  return {
    type: ExperimentActionType.DELETE_CONSTRAINT,
    payload: { index },
  };
};

export const updateConstraint = (index: number, constraint: Constraint) => {
  return {
    type: ExperimentActionType.UPDATE_CONSTRAINT,
    payload: { index, constraint },
  };
};

export const setConstraints = (constraints: Constraint[]) => {
  return {
    type: ExperimentActionType.SET_CONSTRAINTS,
    payload: { constraints },
  };
};

export const setSlottingByGroup = (slottingByGroup: Constraint) => {
  return {
    type: ExperimentActionType.SET_SLOTTING_BY_GROUP,
    payload: { slottingByGroup },
  };
};

// Action type union for type safety
export type ExperimentAction =
  | ReturnType<typeof getExperiment>
  | ReturnType<typeof setExperiment>
  | ReturnType<typeof cloneExperiment>
  | ReturnType<typeof setConfiguration>
  | ReturnType<typeof updateExperiment>
  | ReturnType<typeof updateConfiguration>
  | ReturnType<typeof addExperimentVariable>
  | ReturnType<typeof deleteExperimentVariable>
  | ReturnType<typeof addTreatment>
  | ReturnType<typeof deleteTreatment>
  | ReturnType<typeof addConfiguration>
  | ReturnType<typeof deleteConfiguration>
  | ReturnType<typeof cloneConfiguration>
  | ReturnType<typeof editTreatment>
  | ReturnType<typeof updateAssignmentStrategy>
  | ReturnType<typeof splitTreatmentsEvenly>
  | ReturnType<typeof setVariableTechFamilies>
  | ReturnType<typeof setVariables>
  | ReturnType<typeof setAnalysis>
  | ReturnType<typeof updateRestrictByConstraint>
  | ReturnType<typeof updateValidExperiment>
  | ReturnType<typeof updateValidMetric>
  | ReturnType<typeof addConstraint>
  | ReturnType<typeof deleteConstraint>
  | ReturnType<typeof updateConstraint>
  | ReturnType<typeof setConstraints>
  | ReturnType<typeof setSlottingByGroup>;
